/**
 * Tracking helpers — fires conversion events to all configured analytics platforms.
 *
 * Platforms: Google Ads (gtag), GA4 (gtag), Meta Pixel (fbq), Clarity (window.clarity).
 * If a platform's ID isn't set in Admin → Branding, its script never loads and the
 * corresponding call is a no-op. Backend mirror via POST /api/track/event for redundancy.
 */
import { api } from "@/lib/api";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const STORAGE_KEY = "obs_utm_v1";
// Dev-only debug logger (silent in production builds)
const debug = (...a) => { if (process.env.NODE_ENV !== "production") console.debug(...a); };

const TTL_DAYS = 30;

/** Capture UTM + referrer + landing path on first page load (persists 30 days). */
export function captureUtmsOnce() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const fromUrl = Object.fromEntries(
    UTM_KEYS.filter((k) => url.searchParams.get(k)).map((k) => [k, url.searchParams.get(k)]),
  );
  // Always refresh on a campaign hit, otherwise keep previous attribution
  const existing = readUtms();
  if (Object.keys(fromUrl).length === 0 && existing) return;
  const snapshot = {
    ...fromUrl,
    referrer: document.referrer || "",
    landing_page: url.pathname + url.search,
    captured_at: new Date().toISOString(),
    expires_at: new Date(Date.now() + TTL_DAYS * 86400_000).toISOString(),
  };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot)); }
  catch (e) { debug("track: localStorage unavailable (private mode?)", e); }
}

export function readUtms() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.expires_at && new Date(data.expires_at).getTime() < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch (e) { debug("track: readUtms failed", e); return null; }
}

/**
 * Fire a conversion event to all enabled platforms.
 *   eventName  e.g. "lead", "consultation", "franchise_application", "whatsapp_click"
 *   options    { value: number, currency: "USD", payload: {...}, gtagSendTo?: string }
 *
 * Google Ads conversion labels:
 *   - If `options.gtagSendTo` is passed, it's used as-is.
 *   - Otherwise we look up `window.__obs_tracking__.conversion_labels[eventName]` and
 *     combine with `window.__obs_tracking__.google_ads_id` to build `AW-XXX/LABEL`.
 *   - If no label is configured for this event, no conversion event is fired (the
 *     generic gtag event still fires).
 */
export function trackConversion(eventName, options = {}) {
  const value = Number(options.value || 0);
  const currency = options.currency || "USD";
  const payload = options.payload || {};

  // Resolve per-event Google Ads conversion send_to from window tracking ctx
  const ctx = (typeof window !== "undefined" && window.__obs_tracking__) || {};
  const adsId = ctx.google_ads_id || "";
  const labelFromSettings = (ctx.conversion_labels || {})[eventName] || "";
  const sendTo = options.gtagSendTo
    || (adsId && labelFromSettings ? `${adsId}/${labelFromSettings}` : "");

  // Google Ads + GA4 share the gtag function
  if (typeof window.gtag === "function") {
    window.gtag("event", eventName, { value, currency, ...payload });
    if (sendTo) {
      window.gtag("event", "conversion", {
        send_to: sendTo, value, currency,
      });
    }
  }
  // Meta Pixel — fire ONLY for explicitly mapped events so unmapped
  // conversions don't silently become fallback Lead events. The Pixel is
  // scoped to the /canada-company-india landing family via <MetaPixel/>.
  if (typeof window.fbq === "function") {
    const fbEventMap = {
      lead: "Lead",
      contact: "Contact",
      consultation: "Schedule",
      franchise_application: "SubmitApplication",
      brochure_request: "Lead",
      calculator_quote: "Lead",
      whatsapp_click: "Contact",
      phone_click: "Contact",
      payment_completed: "Purchase",
      client_registration: "CompleteRegistration",
    };
    const mapped = fbEventMap[eventName];
    if (mapped) {
      window.fbq("track", mapped, { value, currency, ...payload });
    }
  }
  // Microsoft Clarity custom event
  if (typeof window.clarity === "function") {
    try { window.clarity("event", eventName); }
    catch (e) { debug("track: clarity event failed", e); }
  }
  // Backend mirror — best-effort
  api.post("/track/event", {
    event: eventName,
    value_usd: value,
    page: typeof window !== "undefined" ? window.location.pathname : "",
    utm: readUtms() || {},
    referrer: typeof document !== "undefined" ? document.referrer : "",
  }).catch(() => { /* fire-and-forget */ });
}

/** Wrap a click handler to emit a conversion before continuing. */
export function withTracking(eventName, fn, opts = {}) {
  return (e) => {
    trackConversion(eventName, opts);
    if (typeof fn === "function") return fn(e);
  };
}
